// src/utils/storyElements.js
//
// Pure helpers for the editor's Story Elements board.
//
// The board stores two things: typed nodes (scene / actor / artifact) and
// labelled edges between them. Chapters are not stored — they're derived from
// the manuscript's heading blocks by outline.js — so an edge endpoint is a
// *reference string* rather than a foreign key:
//
//     "node:<node _id>"     an element on the board
//     "chapter:<blockId>"   a heading in the manuscript
//
// That indirection is the whole reason these functions exist. Resolving a
// reference means looking in two different places, and a chapter reference can
// resolve to nothing at all once the author deletes the heading — which is a
// normal state ("unplaced"), not an error.

/** Split a reference into its kind and id. Returns null for anything malformed. */
export function parseRef(ref) {
  if (typeof ref !== 'string') return null;
  const at = ref.indexOf(':');
  if (at <= 0) return null;
  const kind = ref.slice(0, at);
  const id = ref.slice(at + 1);
  if (!id || (kind !== 'node' && kind !== 'chapter')) return null;
  return { kind, id };
}

/**
 * Flatten the outline tree into the chapters an edge can point at.
 *
 * buildOutline returns a nest of headings; relationships don't care about
 * depth, only about which heading a reference names.
 */
export function flattenOutline(outline = []) {
  const flat = [];
  const walk = (nodes) => {
    (nodes || []).forEach((n) => {
      if (n.blockId) flat.push({ blockId: n.blockId, text: n.text || '', level: n.level });
      if (n.children?.length) walk(n.children);
    });
  };
  walk(outline);
  return flat;
}

/**
 * Resolve a reference to something displayable.
 *
 * Always returns an object — never null — because a reference that no longer
 * resolves still has to render. `missing: true` is the "unplaced" case: the
 * author deleted the heading this relationship pointed at. The same exposure
 * comments already carry, and the reason the UI needs a name for it.
 */
export function describeRef(ref, { nodes = [], chapters = [] } = {}) {
  const parsed = parseRef(ref);
  if (!parsed) return { ref, kind: null, type: null, name: 'Unknown', missing: true };

  if (parsed.kind === 'chapter') {
    const chapter = chapters.find((c) => c.blockId === parsed.id);
    return chapter
      ? { ref, kind: 'chapter', type: 'chapter', name: chapter.text || 'Untitled chapter', blockId: chapter.blockId, missing: false }
      : { ref, kind: 'chapter', type: 'chapter', name: 'Deleted chapter', blockId: parsed.id, missing: true };
  }

  const node = nodes.find((n) => String(n._id) === parsed.id);
  return node
    ? { ref, kind: 'node', type: node.type, name: node.name || 'Untitled', nodeId: parsed.id, missing: false }
    : { ref, kind: 'node', type: null, name: 'Deleted element', nodeId: parsed.id, missing: true };
}

/** Every edge touching `ref`, in either direction. */
export function edgesFor(ref, edges = []) {
  if (!ref) return [];
  return edges.filter((e) => e.from === ref || e.to === ref);
}

/** The end of `edge` that isn't `ref`. */
export function otherEnd(edge, ref) {
  if (!edge) return null;
  return edge.from === ref ? edge.to : edge.from;
}

/**
 * The relationships of one element, resolved for display.
 *
 * `outgoing` matters to the wording: the Outline view reads an edge as a
 * sentence, and "Actor 1 — appears in — Chapter 1" is not the same claim as
 * "Chapter 1 — appears in — Actor 1".
 */
export function relationshipsFor(ref, { edges = [], nodes = [], chapters = [] } = {}) {
  return edgesFor(ref, edges).map((edge) => ({
    edge,
    outgoing: edge.from === ref,
    other: describeRef(otherEnd(edge, ref), { nodes, chapters }),
  }));
}

/**
 * Where in the manuscript an element appears — the chapters you can actually
 * jump to from it.
 *
 * Two hops, not one, and the second hop is the point. In the prototype a scene
 * *was* a chapter, so walking one edge from an actor reached the manuscript.
 * Here scenes and chapters are separate, so the same question — "where does
 * Actor 1 appear?" — has to follow the actor to the Shop scene and then to the
 * chapter the Shop is set in. Stopping at one hop would report an actor as
 * unplaced whenever the author placed the scene rather than the actor.
 *
 * Direct chapter links come first, then chapters reached through a linked
 * element, deduplicated and in manuscript order — so cycling through them walks
 * the book forwards rather than in whatever order the edges were created.
 */
export function locationsFor(ref, { edges = [], nodes = [], chapters = [] } = {}) {
  if (!ref) return [];
  const seen = new Set();
  const direct = [];
  const viaElement = [];

  const collect = (candidate, bucket) => {
    const parsed = parseRef(candidate);
    if (!parsed || parsed.kind !== 'chapter') return;
    if (seen.has(parsed.id)) return;
    seen.add(parsed.id);
    bucket.push(parsed.id);
  };

  const firstHop = edgesFor(ref, edges);
  firstHop.forEach((e) => collect(otherEnd(e, ref), direct));

  firstHop.forEach((e) => {
    const neighbour = otherEnd(e, ref);
    if (parseRef(neighbour)?.kind !== 'node') return;
    edgesFor(neighbour, edges).forEach((e2) => {
      const far = otherEnd(e2, neighbour);
      if (far !== ref) collect(far, viaElement);
    });
  });

  // Manuscript order, so "2 of 3" means the second chapter in the book.
  const order = new Map(chapters.map((c, i) => [c.blockId, i]));
  return [...direct, ...viaElement]
    .sort((a, b) => (order.get(a) ?? Infinity) - (order.get(b) ?? Infinity))
    .map((blockId) => describeRef(`chapter:${blockId}`, { nodes, chapters }))
    // A chapter whose heading the author deleted is not somewhere you can jump.
    .filter((d) => !d.missing);
}

/**
 * Every relationship on the board as a readable sentence, for the Outline view.
 *
 * Grouped by the left-hand element and alphabetical within it, so the list has
 * a stable order the author can scan rather than insertion order that shuffles
 * as elements are connected.
 */
export function relationshipSentences({ edges = [], nodes = [], chapters = [] } = {}) {
  return edges
    .map((edge) => ({
      edge,
      from: describeRef(edge.from, { nodes, chapters }),
      to: describeRef(edge.to, { nodes, chapters }),
      label: edge.label || 'connected to',
    }))
    .sort((a, b) =>
      a.from.name.localeCompare(b.from.name) ||
      a.label.localeCompare(b.label) ||
      a.to.name.localeCompare(b.to.name));
}

/**
 * Everything a new relationship could point at, excluding `excludeRef` itself.
 *
 * Chapters come first: an element's first connection is almost always to the
 * place in the manuscript where it appears.
 */
export function connectTargets({ nodes = [], chapters = [], excludeRef = null } = {}) {
  const typeLabel = { scene: 'Scene', actor: 'Actor', artifact: 'Artifact' };
  return [
    ...chapters.map((c) => ({
      value: `chapter:${c.blockId}`,
      label: `Chapter: ${c.text || 'Untitled'}`,
    })),
    ...nodes.map((n) => ({
      value: `node:${n._id}`,
      label: `${typeLabel[n.type] || n.type}: ${n.name}`,
    })),
  ].filter((o) => o.value !== excludeRef);
}
